import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { EventEmitter, Injectable, Output } from '@angular/core'
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms'
import { catchError, Observable, throwError } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { SnackbarService } from '../../../core/services/snackbar.service'
import { Team } from '../../../core/models/team.model'
import { Player } from '../../../core/models/player.model'
import { User } from '../../../core/models/auth.model'
import { Article, Rumour } from 'src/app/core/models/rumour.model'
import { MetricsService } from '../../atleti-metrics/services/atleti-metrics.service'
import { Match } from 'src/app/core/models/match.model'
import { Target } from '@angular/compiler'

@Injectable({
  providedIn: 'root'
})
export class NewsService {
    constructor(
        private fb:FormBuilder, 
        private http: HttpClient,
        private snackbarService: SnackbarService,
        private playersService: MetricsService
    ) {
        this.clearNews()
    }

    readonly baseURL = environment.DOMAIN_URL+'/api/'

    keywords: string[] = []
    keywordInput = new FormControl('')
    newComment = new FormControl('')

    opened: boolean = false

    positionMap: any = {
        POR: { x: 400, y: 1250 },
        DFC: { x: 400, y: 1080 },
        DFI: { x: 250, y: 1050 },
        DFD: { x: 550, y: 1050 },
        LD: { x: 700, y: 1000 },
        LI: { x: 100, y: 1000 },
        CAD: { x: 760, y: 800 },
        CAI: { x: 40, y: 800 },
        MCD: { x: 400, y: 820 },
        MC: { x: 400, y: 675 },
        MCO: { x: 400, y: 450 },
        MD: { x: 700, y: 550 },
        MI: { x: 100, y: 550 },
        DC: { x: 400, y: 150 },
        SD: { x: 400, y: 250 },
        ED: { x: 650, y: 300 },
        EI: { x: 150, y: 300 },
    }

    positionCategoryMap: { [category: string]: string[] } = {
        'Portero': ['POR'],
        'Defensa': ['DFC', 'DFI', 'DFD', 'LD', 'LI', 'CAD', 'CAI'], 
        'Centrocampista': ['MCD', 'MC', 'MCO', 'MD', 'MI'],  
        'Delantero': ['DC', 'SD', 'ED', 'EI'], 
    }
    
    getPositionCategory(position: string): string {
        for (const category in this.positionCategoryMap) {
            if (this.positionCategoryMap[category].includes(position))
                return category
        }
        return ''
    }

    getMostRecentDate(newsArray: any[]){
        if (!newsArray.length) return 0
        return Math.max(...newsArray.map(n => new Date(n.date).getTime()))
    }

    getRumorType(rumor: Rumour) {
        if(rumor.probability == 'NULL') return 'DESCARTADO'
        if(rumor.type == 'VENTA') return 'SALIDA'
        return rumor.type
    }

    posibilidadConfig = {
        HIGH: { value: 75, color: '#50b2dc' },
        MEDIUM: { value: 50, color: '#4464ad' },
        LOW: { value: 25, color: '#c30913' },
        OFFICIAL: { value: 100, color: '#1f8c0d' },
        NULL: { value: 0, color: '#121215' }
    }
    
    upgradesForm = this.fb.group({
      upgrade: [''],
    })

    articleForm = this.fb.group({
      title: [''],
      description: [''],
      body: [''],
      category: [''],
      state: [''],
      user: this.fb.control<User | null>(null),
      associatedMatch: this.fb.control<Match | null>(null),
      alt: [''],
      username: ['']
    })

    rumorForm = this.fb.group({
        _id : [''],
        category : ['General', Validators.required],
        player: this.fb.control<Player | null>(null, Validators.required),
        team: this.fb.control<Team | null>(null, Validators.required),
        type: ['', Validators.required],
        similarPlayers: this.fb.control<Player[] | null>(null),
        positions: this.fb.group({
            generalPosition: [''],
            mainPosition: [''],
            secondaryPositions: this.fb.control<string[] | null>(null),
        }),
        probability: [''],
        quote: this.fb.group({
            author: this.fb.control<User | null>(null),
            text: ['']
        }),
        youtubeLink: ['', [Validators.pattern(/^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[a-zA-Z0-9_-]*/)]],
        news: this.fb.array([], Validators.required),
        imagesUploaded: this.fb.array([])
    })
    
    get news(): FormArray {
        return this.rumorForm.get('news') as FormArray
    }
    get imagesUploaded(): FormArray {
        return this.rumorForm.get('imagesUploaded') as FormArray
    }
        
    clearNews(){
        this.news.clear()
        this.imagesUploaded.clear()
        this.addNews()
    }
    addNews(){
        this.news.push(this.fb.group({
            source: [''],
            date: [new Date(),Validators.required],
            description: [''],
            link: [''],
        }))
    }
    addImages(){
        this.imagesUploaded.push(this.fb.group({
            name: [''],
            date: [new Date(),Validators.required],
            imgUrl: [''],
        }))
    }

    rumourFilters = this.fb.group({
        type: [''],
        position: this.fb.control<string | null>(null),
        sources: this.fb.control<string[] | null>(null),
        probability: this.fb.control<string | null>(null),
        fromDate: this.fb.control<Date | null>(null),
        toDate: this.fb.control<Date | null>(null),
        player: this.fb.control<Player | null>(null),
        category: this.fb.control<string | null>(null),
    })
    playerFilter = new FormControl('')
    positionFilter = new FormControl('')
    authorFilter = new FormControl('')
    sortBy: string = 'date'
    
    @Output() filtersSubmittedEvent = new EventEmitter<null>()
    @Output() sortChangeEvent = new EventEmitter<null>()
    
    FiltersSubmitted(){
        this.filtersSubmittedEvent.emit()
    }
    SortChanged(){
        this.sortChangeEvent.emit()
    }
  
    selectedFiles: File[] = []

    isJpg(url: string | undefined):boolean {
        if(!url) return false
        return url.includes('.jpg')
    }

    //API CALLS
    getItemById(id: string){
        return this.http.get<any>(`${this.baseURL}rumour/${id}`)
    }

    uploadFoto(file: File){
        return this.http.post(this.baseURL+'upload', file)
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    fetchUpgrades(){
        return this.http.get(this.baseURL+'upgrades')
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    postUpgrade() {
        return this.http.post(this.baseURL+'upgrades', this.upgradesForm.value)
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    fetchRumours(){
        return this.http.get(this.baseURL+'rumour')
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    fetchNews(){
        return this.http.get(this.baseURL+'rumour/news')
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    fetchRumourAuthors(){
        return this.http.get(this.baseURL+'rumour/authors')
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    fetchRumourCounters(){
        let params = new HttpParams()
        const category = this.rumourFilters.get('category')?.value
        if (category) params = params.set('category', category)

        return this.http.get(this.baseURL+'rumour/counters/', { params })
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    fetchFilteredRumours(){
        const filters = this.rumourFilters.getRawValue()
        return this.http.post(this.baseURL+'rumour/filters', filters)
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    createArticle() {
        const formData = this.createFormData({...this.articleForm.value, keywords: this.keywords, associatedPlayers: this.playersService.playersSelected})
        return this.http.post(this.baseURL+'articles', formData)
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    fetchArticles() {
        return this.http.get(this.baseURL+'articles')
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    deleteArticles(articleIDs: string[]){
        return this.http.post(this.baseURL+'articles/delete/', articleIDs)
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    getArticleById(id: string): Observable<Article> {
        return this.http.get<Article>(this.baseURL+`articles/${id}`)
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    updateArticle(id: string) {
        const formData = this.createFormData({...this.articleForm.value, keywords: this.keywords, associatedPlayers: this.playersService.playersSelected})
        return this.http.put(this.baseURL+`articles/${id}`, formData)
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    likeArticle (id: string) {
        return this.http.patch<{ likes: number }>(this.baseURL+`articles/${id}/like`, {})
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    incrementViews(id: string, path: string) {
        return this.http.patch<{ views: number }>(this.baseURL+path+`/${id}/view`, {})
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    voteRumour(id: string, vote: string) {
        return this.http.patch(this.baseURL+`rumour/${id}/vote`, {vote})
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    /* COMENTARIOS */
    private buildUrl(target: string, id: string) {
        return this.baseURL+`${target}/${id}/comments`
    }
    getComments(id: string, target: string = 'articles') {
        return this.http.get(this.buildUrl(target, id))
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    postComment(id: string, target: string = 'articles' ) {
        return this.http.post(this.buildUrl(target, id), { content: this.newComment.value })
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    getProfileImage(author: any): string {
        return author?.profileImgUrl || 'https://atleticostats.fra1.cdn.digitaloceanspaces.com/users/DefaultProfile.webp';
    }

    postImage(form: FormData) {
        return this.http.put(this.baseURL+`articles/foto`, form)
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    createFormData(form: any): FormData{
        const formData = new FormData()

        this.selectedFiles.forEach((file, index) => {
          formData.append('file', file)
        })
        
        const appendFormData = (formData: FormData, key: string, value: any) => {
            if (value === null || value === undefined) return
            
            if (value instanceof Date) formData.append(key, value.toISOString())
            else if (value && value._id) formData.append(key, value._id)
            else if (Array.isArray(value)) {
                if (value.length > 0) {
                    value.forEach((item, index) => {
                        if (typeof item === 'object' && item !== null) {
                            Object.keys(item).forEach((subKey) => {
                                let subValue = item[subKey]
                                if (subValue instanceof Date) subValue = subValue.toISOString()
                                formData.append(`${key}[${index}][${subKey}]`, subValue ?? '')
                            })
                        } else {
                            formData.append(`${key}[${index}]`, item ?? '')
                        }
                    })
                }             
            } else if (typeof value === 'object' && value !== null) {
                Object.keys(value).forEach((subKey) => {
                    appendFormData(formData, `${key}.${subKey}`, value[subKey])
                })
            } else formData.append(key, value)
        }
    
        Object.keys(form).forEach((key) => {
            let value = form[key]
    
            if (key === 'positions' && typeof value === 'object') {
                if (Array.isArray(value.mainPosition)) {
                    value.mainPosition = value.mainPosition.length ? value.mainPosition[0] : ''
                }
                if (Array.isArray(value.generalPosition)) {
                    value.generalPosition = value.generalPosition.length ? value.generalPosition[0] : ''
                }
            }
    
            appendFormData(formData, key, value)
        })

        return formData
    }

    postRumor(){
        const formData = this.createFormData(this.rumorForm.value)
        return this.http.post(this.baseURL+'rumour', formData)
        .pipe(catchError(this.errorHandler.bind(this)))
    }
    
    putRumor(){
        const formData = this.createFormData(this.rumorForm.value)
        return this.http.put(this.baseURL+'rumour/'+this.rumorForm.get('_id')?.value, formData)
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    deleteRumours(rumourIDs: string[]){
        return this.http.post(this.baseURL+'rumour/delete/', rumourIDs)
        .pipe(catchError(this.errorHandler.bind(this)))
    }

    // Files 
    handleFileUpload(file: File){
        this.selectedFiles.push(file)
    }
    clearFiles() {
        this.selectedFiles = []
    }
    getFileNames(): string {
        return this.selectedFiles.map(file => file.name).join(', ')
    }

    //ERROR HANDLER
    private errorHandler(error: HttpErrorResponse) {
        if (error.status === 0) console.error('An error occurred:', error.error)
        else this.snackbarService.showSnackbar(error.message || 'An error occurred')
        return throwError(() => new Error())
    }
}